import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  deleteObject as apiDeleteMapObject,
  newObject as apiNewMapObject,
  updateObject as apiUpdateMapObject,
} from '~/api/projects2';
import { captureTypedPosthogEvent, mapObjectTypeToCivildrawObjectType, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { MapDetail, MapObject } from '~/common/types';
import type { NewMapObject } from '~/pages/projectDetails/civilDrawV2/civildrawCanvasAdapter';
import rootStore from '~/store';
import { civilDrawMapQueryKey } from './useCivilDrawMap';

const MUTATION_KEY = ['civilDrawMapObject'] as const;

type MapIds = { projectId: string; mapId: string };

type SaveVariables = { ids: MapIds; mapObject: MapObject; before: MapObject };

const eventProperties = ({ projectId }: MapIds, type: MapObject['type']) => ({
  project_id: projectId,
  organization_id: rootStore.currentOrganizationId || undefined,
  object_type: mapObjectTypeToCivildrawObjectType(type),
  civildraw_use_new_drawing_canvas: true,
});

/** Creates, updates and deletes CivilDraw map objects, changing the cached map before each save. */
export function useMapObjectMutations(projectId: string, mapId: string) {
  const queryClient = useQueryClient();

  const getMap = (ids: MapIds) => {
    const map = queryClient.getQueryData<MapDetail>(civilDrawMapQueryKey(ids.projectId, ids.mapId));
    if (!map) throw new Error(`CivilDraw map ${ids.mapId} isn't loaded`);
    return map;
  };

  const getMapObject = (ids: MapIds, mapObjectId: string) =>
    queryClient.getQueryData<MapDetail>(civilDrawMapQueryKey(ids.projectId, ids.mapId))?.objects[mapObjectId];

  const setMapObject = (ids: MapIds, mapObject: MapObject) =>
    queryClient.setQueryData<MapDetail>(
      civilDrawMapQueryKey(ids.projectId, ids.mapId),
      (map) => map && { ...map, objects: { ...map.objects, [mapObject.id]: mapObject } },
    );

  const removeMapObject = (ids: MapIds, mapObjectId: string) =>
    queryClient.setQueryData<MapDetail>(civilDrawMapQueryKey(ids.projectId, ids.mapId), (map) => {
      if (!map) return map;
      const { [mapObjectId]: _removed, ...mapObjects } = map.objects;
      return { ...map, objects: mapObjects };
    });

  // One map's saves run in order, so an edit or delete never reaches the server before its map object's create
  const scope = { id: `civilDrawMap:${projectId}:${mapId}` };

  const createMutation = useMutation({
    mutationKey: MUTATION_KEY,
    scope,
    mutationFn: ({ ids, mapObject }: { ids: MapIds; mapObject: NewMapObject }) =>
      apiNewMapObject(ids.projectId, getMap(ids), mapObject),
    onSuccess: (_saved, { ids, mapObject }) => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectCivildrawDrawingCreated, {
        ...eventProperties(ids, mapObject.type),
        drawing_id: mapObject.id,
      });
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectCivildrawObjectAdded, eventProperties(ids, mapObject.type));
    },
    onError: (_error, { ids, mapObject }) => removeMapObject(ids, mapObject.id),
  });

  const updateMutation = useMutation({
    mutationKey: MUTATION_KEY,
    scope,
    mutationFn: ({ ids, mapObject }: SaveVariables) => apiUpdateMapObject(ids.projectId, getMap(ids), mapObject),
    onSuccess: (_saved, { ids, mapObject }) => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectGeometryUpdated, {
        ...eventProperties(ids, mapObject.type),
        map_id: ids.mapId,
      });
    },
    onError: (_error, { ids, mapObject, before }) => {
      if (getMapObject(ids, mapObject.id)) setMapObject(ids, before);
    },
  });

  const deleteMutation = useMutation({
    mutationKey: MUTATION_KEY,
    scope,
    mutationFn: ({ ids, mapObject }: { ids: MapIds; mapObject: MapObject }) =>
      apiDeleteMapObject(ids.projectId, getMap(ids), mapObject),
    onSuccess: (_deleted, { ids, mapObject }) => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectDeleted, {
        ...eventProperties(ids, mapObject.type),
        map_id: ids.mapId,
      });
    },
    onError: (_error, { ids, mapObject }) => setMapObject(ids, mapObject),
  });

  useEffect(() => {
    const warnWhileSaving = (event: BeforeUnloadEvent) => {
      if (queryClient.isMutating({ mutationKey: MUTATION_KEY }) > 0) event.preventDefault();
    };
    window.addEventListener('beforeunload', warnWhileSaving);
    return () => window.removeEventListener('beforeunload', warnWhileSaving);
  }, [queryClient]);

  const createMapObject = (mapObject: NewMapObject) => {
    const ids = { projectId, mapId };
    // The server fills these in, and nothing in the new view reads them
    setMapObject(ids, { ...mapObject, createdBy: { name: '' }, updatedBy: { name: '' } });
    createMutation.mutate({ ids, mapObject });
  };

  const updateMapObject = (mapObject: MapObject) => {
    const ids = { projectId, mapId };
    const before = getMapObject(ids, mapObject.id);
    if (!before) return;
    setMapObject(ids, mapObject);
    updateMutation.mutate({ ids, mapObject, before });
  };

  const deleteMapObject = (mapObjectId: string) => {
    const ids = { projectId, mapId };
    const mapObject = getMapObject(ids, mapObjectId);
    if (!mapObject) return;
    removeMapObject(ids, mapObjectId);
    deleteMutation.mutate({ ids, mapObject });
  };

  return { createMapObject, updateMapObject, deleteMapObject };
}
